import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import type {
  InspectionTemplate,
  TemplateItem,
  TemplateSection,
} from 'inspection-types';
import {
  useAddItemFromTemplate,
  useAddSectionFromTemplate,
  useInspectionsTemplateLibraryEnabled,
  useRemoveItemFromTemplate,
  useRemoveSectionFromTemplate,
} from 'react-hooks';

import ChannelAdminContext from '../../../../pages/portal/admin/channel/ChannelAdminContext';
import { TEMPLATE_LIBRARY_PATH } from '../../navigation/useInspectionsNavigation';

import {
  cloneTemplateSection,
  deriveInspectionTemplateAfterSectionRemoved,
  mergeRemovedSectionSnapshotsIntoOrderedSections,
} from '../../helpers/inspectionTemplateCustomizeHelpers';

import {
  projectInspectionTemplateCustomizeFromBaselineDraft,
} from '../../helpers/baselineDraftCustomizeProjection';

import { useInspectionTemplateCustomizeExpandedState } from './useInspectionTemplateCustomizeExpandedState';
import { useInspectionTemplateCustomizeLabels } from './useInspectionTemplateCustomizeLabels';
import { useInspectionTemplateCustomizeRouteFetch } from './useInspectionTemplateCustomizeRouteFetch';
import { useInspectionTemplateCustomizeSectionObserver } from './useInspectionTemplateCustomizeSectionObserver';

export type RemovedItemSnapshot = {
  sectionId: string;
  item: TemplateItem;
};

export type InspectionTemplateCustomizePageViewModel = {
  listPageUrl: string;
  hasValidRouteContext: boolean;
  template: InspectionTemplate | undefined;
  /** Present when the API returns a parent blueprint alongside the draft `template`. */
  baselineTemplate: InspectionTemplate | undefined;
  loading: boolean;
  error: Error | undefined;
  called: boolean;
  sortedSections: TemplateSection[];
  expandedBySectionId: Record<string, boolean>;
  activeSectionId: string | null;
  mainColumnRefCallback: (el: HTMLDivElement | null) => void;
  toggleSection: (sectionId: string) => void;
  handleCancel: () => void;
  summary: string | null;
  lastUpdatedText: string;
  saveDisabledTitle: string;
  revertAllDisabledTitle: string;
  removeSectionDisabledTitle: string;
  removeItemDisabledTitle: string;
  /** Item ids currently undergoing a remove mutation (supports concurrent removes). */
  removingItemIds: ReadonlySet<string>;
  /** Item ids currently undergoing a restore (supports concurrent restores). */
  restoringItemIds: ReadonlySet<string>;
  /** Section ids currently undergoing a remove mutation (supports concurrent removes). */
  removingSectionIds: ReadonlySet<string>;
  /** Section ids currently undergoing a restore mutation (supports concurrent restores). */
  restoringSectionIds: ReadonlySet<string>;
  /** Matches {@link useInspectionsTemplateLibraryEnabled} — gates restore on same entry as this page. */
  inspectionsTemplateLibraryEnabled: boolean;
  removeTemplateItem: (itemId: string) => Promise<void>;
  removeTemplateSection: (sectionId: string) => Promise<void>;
  restoreTemplateItem: (itemId: string) => Promise<void>;
  restoreTemplateSection: (sectionId: string) => Promise<void>;
  /**
   * Item IDs rendered with removed (strikethrough) presentation — includes
   * per-item overlays and every item under a soft-removed section.
   */
  removedOverlayItemIds: ReadonlySet<string>;
  /** Section IDs shown as removed (server section gone; client snapshot only). */
  removedSectionPresentationIds: ReadonlySet<string>;
};

function findSectionAndItem(
  tmpl: InspectionTemplate,
  itemId: string
): { sectionId: string; item: TemplateItem } | undefined {
  for (const sec of tmpl.sections) {
    const item = sec.items.find(i => i.id === itemId);
    if (item) {
      return { sectionId: sec.id, item };
    }
  }
  return undefined;
}

function mergeSectionsWithRemovedOverlays(
  sections: TemplateSection[],
  removedSnapshots: Record<string, RemovedItemSnapshot>
): TemplateSection[] {
  return sections.map(section => {
    const serverIds = new Set(section.items.map(i => i.id));
    const overlayItems = Object.values(removedSnapshots)
      .filter(s => s.sectionId === section.id && !serverIds.has(s.item.id))
      .map(s => s.item);

    if (overlayItems.length === 0) {
      return {
        ...section,
        items: [...section.items].sort((a, b) => a.order - b.order),
      };
    }

    const items = [...section.items, ...overlayItems].sort(
      (a, b) => a.order - b.order
    );
    return { ...section, items };
  });
}

export function useInspectionTemplateCustomizePage(
  channelBasePath: string
): InspectionTemplateCustomizePageViewModel {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { channel } = useContext(ChannelAdminContext);
  const { templateId } = useParams<{ templateId: string }>();
  const mainColumnRef = useRef<HTMLDivElement | null>(null);

  const { removeItemFromTemplate: runRemoveItemMutation } =
    useRemoveItemFromTemplate();
  const { addItemFromTemplate: runAddItemMutation } = useAddItemFromTemplate();
  const { addSectionFromTemplate: runAddSectionMutation } =
    useAddSectionFromTemplate();
  const { removeSectionFromTemplate: runRemoveSectionMutation } =
    useRemoveSectionFromTemplate();
  const inspectionsTemplateLibraryEnabled =
    useInspectionsTemplateLibraryEnabled();

  const {
    template,
    baselineTemplate,
    loading,
    error,
    called,
    fetchCurrentInspectionTemplate,
  } = useInspectionTemplateCustomizeRouteFetch(channel?._id, templateId);

  const templateRef = useRef(template);
  templateRef.current = template;

  const baselineTemplateRef = useRef(baselineTemplate);
  baselineTemplateRef.current = baselineTemplate;

  const [expandedBySectionId, setExpandedBySectionId] = useState<
    Record<string, boolean>
  >({});
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [removingItemIds, setRemovingItemIds] = useState(
    () => new Set<string>()
  );
  const [restoringItemIds, setRestoringItemIds] = useState(
    () => new Set<string>()
  );
  const [removingSectionIds, setRemovingSectionIds] = useState(
    () => new Set<string>()
  );
  const [restoringSectionIds, setRestoringSectionIds] = useState(
    () => new Set<string>()
  );
  const [removedSnapshots, setRemovedSnapshots] = useState<
    Record<string, RemovedItemSnapshot>
  >({});
  const [removedSectionSnapshots, setRemovedSectionSnapshots] = useState<
    Record<string, TemplateSection>
  >({});

  const removingItemIdsRef = useRef(removingItemIds);
  removingItemIdsRef.current = removingItemIds;
  const restoringItemIdsRef = useRef(restoringItemIds);
  restoringItemIdsRef.current = restoringItemIds;
  const removingSectionIdsRef = useRef(removingSectionIds);
  removingSectionIdsRef.current = removingSectionIds;
  const restoringSectionIdsRef = useRef(restoringSectionIds);
  restoringSectionIdsRef.current = restoringSectionIds;

  /** Always latest overlays; restores read here to avoid stale `removedSnapshots` in callbacks. */
  const removedSnapshotsRef = useRef(removedSnapshots);
  removedSnapshotsRef.current = removedSnapshots;

  const removedSectionSnapshotsRef = useRef(removedSectionSnapshots);
  removedSectionSnapshotsRef.current = removedSectionSnapshots;

  const listPageUrl = `${channelBasePath}/${TEMPLATE_LIBRARY_PATH}`;

  /**
   * Strikethrough / undo for removed sections and questions applies only for the
   * current editor visit. Each navigation gets a new `location.key` (e.g. list →
   * edit again), so we drop client overlays and show only `currentInspectionTemplate`.
   */
  useLayoutEffect(() => {
    if (!channel?._id || !templateId) {
      return;
    }
    setRemovedSectionSnapshots({});
    setRemovedSnapshots({});
  }, [location.key, channel?._id, templateId]);

  /** Baseline-driven mode uses server diff instead of overlay snapshots — clear leftovers on refetch. */
  useEffect(() => {
    if (baselineTemplate != null && template != null) {
      setRemovedSnapshots({});
      setRemovedSectionSnapshots({});
    }
  }, [baselineTemplate?.id, template?.id, template?.version]);

  const baselineProjection = useMemo(() => {
    if (!template || !baselineTemplate) {
      return null;
    }

    return projectInspectionTemplateCustomizeFromBaselineDraft(
      baselineTemplate,
      template
    );
  }, [baselineTemplate, template]);

  const sortedSections = useMemo((): TemplateSection[] => {
    if (!template) {
      return [];
    }

    if (baselineProjection) {
      return baselineProjection.sortedSections;
    }

    const ordered = [...template.sections].sort((a, b) => a.order - b.order);
    // Section snapshots are in-session only; cleared on new navigation (see location.key effect).
    const withSectionOverlays =
      mergeRemovedSectionSnapshotsIntoOrderedSections(
        ordered,
        removedSectionSnapshots
      );
    return mergeSectionsWithRemovedOverlays(
      withSectionOverlays,
      removedSnapshots
    );
  }, [
    template,
    baselineProjection,
    removedSnapshots,
    removedSectionSnapshots,
  ]);

  const removedSectionPresentationIds = useMemo(
    () =>
      baselineProjection != null
        ? baselineProjection.removedSectionPresentationIds
        : new Set(Object.keys(removedSectionSnapshots)),
    [baselineProjection, removedSectionSnapshots]
  );

  const removedOverlayItemIds = useMemo(() => {
    if (baselineProjection != null) {
      return new Set(baselineProjection.removedOverlayItemIds);
    }

    const ids = new Set<string>();
    for (const id of Object.keys(removedSnapshots)) {
      ids.add(id);
    }
    for (const sec of Object.values(removedSectionSnapshots)) {
      for (const item of sec.items) {
        ids.add(item.id);
      }
    }

    return ids;
  }, [baselineProjection, removedSnapshots, removedSectionSnapshots]);

  useInspectionTemplateCustomizeExpandedState({
    template,
    sortedSections,
    setExpandedBySectionId,
    setActiveSectionId,
  });

  useInspectionTemplateCustomizeSectionObserver({
    template,
    sortedSections,
    mainColumnRef,
    setActiveSectionId,
  });

  const handleCancel = useCallback(() => {
    history.push(listPageUrl);
  }, [history, listPageUrl]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedBySectionId(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const mainColumnRefCallback = useCallback((el: HTMLDivElement | null) => {
    mainColumnRef.current = el;
  }, []);

  const labels = useInspectionTemplateCustomizeLabels(template, t);

  const hasValidRouteContext = Boolean(channel?._id && templateId);

  const removeTemplateItem = useCallback(
    async (itemId: string) => {
      if (!channel?._id || !templateId) {
        return;
      }

      if (
        removingSectionIdsRef.current.size > 0 ||
        restoringSectionIdsRef.current.size > 0
      ) {
        return;
      }

      const templateAtStart = templateRef.current;
      if (!templateAtStart) {
        return;
      }

      const located = findSectionAndItem(templateAtStart, itemId);
      if (!located) {
        return;
      }

      setRemovingItemIds(prev => new Set(prev).add(itemId));

      let removeSucceeded = false;
      try {
        await runRemoveItemMutation({
          channelId: channel._id,
          templateId,
          itemId,
        });

        if (!baselineTemplateRef.current) {
          setRemovedSnapshots(prev => ({
            ...prev,
            [itemId]: {
              sectionId: located.sectionId,
              item: located.item,
            },
          }));
        }

        await fetchCurrentInspectionTemplate({
          id: templateId,
          channelId: channel._id,
        });

        removeSucceeded = true;
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.removeItemErrorToast')
        );
      } finally {
        setRemovingItemIds(prev => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      }

      if (removeSucceeded) {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.removeItemSuccessToast')
        );
      }
    },
    [
      channel?._id,
      fetchCurrentInspectionTemplate,
      runRemoveItemMutation,
      t,
      templateId,
    ]
  );

  const restoreTemplateItem = useCallback(
    async (itemId: string) => {
      if (!channel?._id || !templateId) {
        return;
      }

      if (
        restoringSectionIdsRef.current.size > 0 ||
        removingSectionIdsRef.current.size > 0
      ) {
        return;
      }

      const baseline = baselineTemplateRef.current;

      let sectionIdForRestore: string;
      let itemForRestore: TemplateItem;

      if (baseline != null) {
        const hit = findSectionAndItem(baseline, itemId);
        if (!hit) {
          await window.Toast.show(
            t('web.admin.inspections.templateCustomize.restoreItemErrorToast')
          );

          return;
        }

        sectionIdForRestore = hit.sectionId;
        itemForRestore = hit.item;
      } else {
        const snapshot = removedSnapshotsRef.current[itemId];
        if (!snapshot) {
          await window.Toast.show(
            t('web.admin.inspections.templateCustomize.restoreItemErrorToast')
          );

          return;
        }

        sectionIdForRestore = snapshot.sectionId;
        itemForRestore = snapshot.item;
      }

      setRestoringItemIds(prev => new Set(prev).add(itemId));

      let restoreSucceeded = false;
      try {
        await runAddItemMutation({
          channelId: channel._id,
          templateId,
          sectionId: sectionIdForRestore,
          item: itemForRestore,
        });

        await fetchCurrentInspectionTemplate({
          id: templateId,
          channelId: channel._id,
        });

        setRemovedSnapshots(prev => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });

        restoreSucceeded = true;
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.restoreItemErrorToast')
        );
      } finally {
        setRestoringItemIds(prev => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      }

      if (restoreSucceeded) {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.restoreItemSuccessToast')
        );
      }
    },
    [
      channel?._id,
      fetchCurrentInspectionTemplate,
      runAddItemMutation,
      t,
      templateId,
    ]
  );

  const restoreTemplateSection = useCallback(
    async (sectionId: string) => {
      if (!channel?._id || !templateId || !inspectionsTemplateLibraryEnabled) {
        return;
      }

      if (restoringSectionIdsRef.current.has(sectionId)) {
        return;
      }

      if (
        removingSectionIdsRef.current.size > 0 ||
        restoringItemIdsRef.current.size > 0 ||
        removingItemIdsRef.current.size > 0
      ) {
        return;
      }

      let sectionPayloadToAdd: TemplateSection;

      const baseline = baselineTemplateRef.current;

      if (baseline != null) {
        const sec = baseline.sections.find(s => s.id === sectionId);
        if (!sec) {
          await window.Toast.show(
            t('web.admin.inspections.templateCustomize.restoreSectionErrorToast')
          );

          return;
        }

        sectionPayloadToAdd = cloneTemplateSection(sec);
      } else {
        const snapshot =
          removedSectionSnapshotsRef.current[sectionId];

        if (!snapshot) {
          await window.Toast.show(
            t('web.admin.inspections.templateCustomize.restoreSectionErrorToast')
          );

          return;
        }

        sectionPayloadToAdd = cloneTemplateSection(snapshot);
      }

      setRestoringSectionIds(prev => new Set(prev).add(sectionId));

      try {
        await runAddSectionMutation({
          channelId: channel._id,
          templateId,
          section: sectionPayloadToAdd,
        });

        await fetchCurrentInspectionTemplate({
          id: templateId,
          channelId: channel._id,
        });

        setRemovedSectionSnapshots(prev => {
          const next = { ...prev };
          delete next[sectionId];
          return next;
        });

        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.restoreSectionSuccessToast')
        );
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.restoreSectionErrorToast')
        );
      } finally {
        setRestoringSectionIds(prev => {
          const next = new Set(prev);
          next.delete(sectionId);
          return next;
        });
      }
    },
    [
      channel?._id,
      fetchCurrentInspectionTemplate,
      inspectionsTemplateLibraryEnabled,
      runAddSectionMutation,
      t,
      templateId,
    ]
  );

  const removeTemplateSection = useCallback(
    async (sectionId: string) => {
      if (!channel?._id || !templateId) {
        return;
      }

      if (removingSectionIdsRef.current.has(sectionId)) {
        return;
      }

      if (
        restoringSectionIdsRef.current.size > 0 ||
        restoringItemIdsRef.current.size > 0 ||
        removingItemIdsRef.current.size > 0
      ) {
        return;
      }

      const templateAtStart = templateRef.current;
      if (!templateAtStart) {
        return;
      }

      const baseSection = templateAtStart.sections.find(s => s.id === sectionId);
      if (!baseSection) {
        return;
      }

      const includeSnapshots = baselineTemplateRef.current == null;

      let mergedItems: TemplateItem[] = [];
      if (includeSnapshots) {
        const overlayItemsForSection = Object.values(
          removedSnapshotsRef.current
        )
          .filter(s => s.sectionId === sectionId)
          .map(s => s.item);

        const itemById = new Map<string, TemplateItem>();
        for (const item of baseSection.items) {
          itemById.set(item.id, item);
        }
        for (const item of overlayItemsForSection) {
          itemById.set(item.id, item);
        }
        mergedItems = [...itemById.values()].sort(
          (a, b) => a.order - b.order
        );
      }

      setRemovingSectionIds(prev => new Set(prev).add(sectionId));

      let removeSucceeded = false;
      try {
        const { newVersion } = await runRemoveSectionMutation({
          channelId: channel._id,
          templateId,
          sectionId,
        });

        const nextTemplate = deriveInspectionTemplateAfterSectionRemoved(
          templateAtStart,
          sectionId,
          newVersion
        );

        if (includeSnapshots) {
          setRemovedSectionSnapshots(prev => ({
            ...prev,
            [sectionId]: cloneTemplateSection({
              ...baseSection,
              items: mergedItems,
            }),
          }));

          setRemovedSnapshots(prev => {
            const next = { ...prev };
            for (const [itemId, snap] of Object.entries(next)) {
              if (snap.sectionId === sectionId) {
                delete next[itemId];
              }
            }

            return next;
          });
        }

        setExpandedBySectionId(prev => {
          const next = { ...prev };
          delete next[sectionId];
          return next;
        });

        setActiveSectionId(prev => {
          if (prev !== sectionId) {
            return prev;
          }
          const ordered = [...nextTemplate.sections].sort(
            (a, b) => a.order - b.order
          );
          return ordered[0]?.id ?? null;
        });

        await fetchCurrentInspectionTemplate({
          id: templateId,
          channelId: channel._id,
        });

        removeSucceeded = true;
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.removeSectionErrorToast')
        );
      } finally {
        setRemovingSectionIds(prev => {
          const next = new Set(prev);
          next.delete(sectionId);
          return next;
        });
      }

      if (removeSucceeded) {
        await window.Toast.show(
          t('web.admin.inspections.templateCustomize.removeSectionSuccessToast')
        );
      }
    },
    [
      channel?._id,
      fetchCurrentInspectionTemplate,
      runRemoveSectionMutation,
      t,
      templateId,
    ]
  );

  return {
    listPageUrl,
    hasValidRouteContext,
    template,
    baselineTemplate,
    loading,
    error,
    called,
    sortedSections,
    expandedBySectionId,
    activeSectionId,
    mainColumnRefCallback,
    toggleSection,
    handleCancel,
    summary: labels.summary,
    lastUpdatedText: labels.lastUpdatedText,
    saveDisabledTitle: labels.saveDisabledTitle,
    revertAllDisabledTitle: labels.revertAllDisabledTitle,
    removeSectionDisabledTitle: labels.removeSectionDisabledTitle,
    removeItemDisabledTitle: labels.removeItemDisabledTitle,
    removingItemIds,
    restoringItemIds,
    removingSectionIds,
    restoringSectionIds,
    inspectionsTemplateLibraryEnabled,
    removeTemplateItem,
    removeTemplateSection,
    restoreTemplateItem,
    restoreTemplateSection,
    removedOverlayItemIds,
    removedSectionPresentationIds,
  };
}
